import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["digit", "form", "hint", "hintText"]
  static values = {
    mode: { type: String, default: "numeric" },
    codeField: { type: String, default: "code" },
    errorMessage: String
  }
  static classes = ["hintError"]

  #handleSubmitEnd = (event) => {
    if (event.detail.success) return
    this.#onFailure(event.detail.fetchResponse?.response?.status)
  }

  get #pattern() {
    return this.modeValue === "alphanumeric" ? /^[a-zA-Z0-9]$/ : /^\d$/
  }

  get #stripPattern() {
    return this.modeValue === "alphanumeric" ? /[^a-zA-Z0-9]/g : /\D/g
  }

  formTargetConnected(form) {
    form.addEventListener("turbo:submit-end", this.#handleSubmitEnd)
  }

  formTargetDisconnected(form) {
    form.removeEventListener("turbo:submit-end", this.#handleSubmitEnd)
  }

  reset() {
    this.digitTargets.forEach(input => input.value = "")
    this.digitTargets[0]?.focus()
  }

  handleFocus(event) {
    event.target.setSelectionRange(0, event.target.value.length)
  }

  handleInput(event) {
    const input = event.target
    const chars = input.value.replace(this.#stripPattern, "")
    const index = this.digitTargets.indexOf(input)

    // Multi-character input (mobile autofill fallback)
    if (chars.length > 1) {
      input.value = ""
      this.#distribute(chars, index)
      return
    }

    // Invalid character entered
    if (chars.length === 0) {
      input.value = ""
      return
    }

    // Single valid character
    input.value = chars

    const next = this.digitTargets[index + 1]
    if (next) next.focus()

    this.#checkComplete()
  }

  handleKeydown(event) {
    const input = event.target
    const index = this.digitTargets.indexOf(input)

    if (event.key === "Backspace") {
      if (input.value !== "") {
        input.value = ""
        event.preventDefault()
      } else if (index > 0) {
        const prev = this.digitTargets[index - 1]
        prev.value = ""
        prev.focus()
        event.preventDefault()
      }
    } else if (this.#pattern.test(event.key) && input.value !== "") {
      // Field already filled — write to next field instead
      event.preventDefault()
      const next = this.digitTargets[index + 1]
      if (next) {
        next.value = event.key
        next.focus()
        this.#checkComplete()
      }
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const chars = event.clipboardData.getData("text").replace(this.#stripPattern, "")
    this.#distribute(chars, this.digitTargets.indexOf(event.target))
  }

  #distribute(chars, startIndex) {
    const targets = this.digitTargets
    chars.slice(0, targets.length - startIndex).split("").forEach((char, i) => {
      if (targets[startIndex + i]) {
        targets[startIndex + i].value = char
      }
    })

    const nextEmpty = targets.find(input => input.value === "")
    if (nextEmpty) {
      nextEmpty.focus()
    } else {
      targets[targets.length - 1].focus()
    }

    this.#checkComplete()
  }

  #checkComplete() {
    if (this.digitTargets.every(input => this.#pattern.test(input.value))) {
      this.#submit()
    }
  }

  #submit() {
    const code = this.digitTargets.map(input => input.value).join("")
    const codeField = this.formTarget.querySelector(`input[name="${this.codeFieldValue}"]`)
    codeField.value = code
    this.formTarget.requestSubmit()
  }

  #onFailure(status) {
    this.reset()
    if (status !== 429) this.#showError()
  }

  #showError() {
    if (!this.errorMessageValue || !this.hasHintTarget) return

    if (this.hasHintTextTarget) {
      this.hintTextTarget.textContent = this.errorMessageValue
    }
    this.hintTarget.classList.remove("text-content-secondary")
    this.hintTarget.classList.add(...this.hintErrorClasses)
  }
}
